import React from 'react';
import { I18n } from '@aws-amplify/core';
import { useHistory } from 'react-router';
import { Row, List, Checkbox, Popconfirm } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

import { REPORT_NAME_KEYS } from '@/utils/constants';

import { UploadedFiles } from '@/components/views/Upload/types';
import { CustomReportDTO } from '@/components/ui/Inputs/types/response/customReport';
import {
	ItemRow,
	FileName,
	Container,
	ErrorIcon,
	HeaderCol,
	VideoIcon,
	DeleteIcon,
	ReportsBar,
	CenteredCol,
	SuccessIcon,
	ReportsButton,
	DescriptionCol,
	LeftAlignedCol,
	UploadProgress,
	DeleteConfirmIcon
} from '@/components/views/Upload/List/styles';

interface ListUploadProps {
	files: UploadedFiles[];
	inProcessUpload: boolean;
	customReports?: CustomReportDTO[];
	onDelete: (id: string) => void;
	onUpdate: (id: string, data: Partial<UploadedFiles>) => void;
}

export default function ListUpload({
	files,
	onDelete,
	onUpdate,
	inProcessUpload,
	customReports = []
}: Readonly<ListUploadProps>): JSX.Element {
	const history = useHistory();

	const seraReport = customReports.find(({ name }) => name === REPORT_NAME_KEYS.SERA);

	const seraReportId = seraReport?.id ?? null;

	const hasSeraReport = !!seraReportId;

	const HeaderList = () => (
		<Row align="middle" justify="center">
			<HeaderCol sm={hasSeraReport ? 14 : 18}>
				<span>{I18n.get('Description')}</span>
			</HeaderCol>
			{hasSeraReport && (
				<LeftAlignedCol sm={4}>
					<span>{I18n.get('Report')}</span>
				</LeftAlignedCol>
			)}
			<CenteredCol sm={2}>
				<span>{I18n.get('Blur')}</span>
			</CenteredCol>
			<CenteredCol sm={4}>
				<span>{I18n.get('Actions')}</span>
			</CenteredCol>
		</Row>
	);

	function showSeraCheckbox(item: UploadedFiles): JSX.Element {
		const isSeraSelected = item.custom_report === seraReportId;

		function handleSeraChange({ target }: CheckboxChangeEvent): void {
			const custom_report = target.checked ? seraReportId : null;

			onUpdate(item.id, { custom_report });
		}

		return (
			<LeftAlignedCol sm={4}>
				<Checkbox checked={isSeraSelected} disabled={inProcessUpload} onChange={handleSeraChange}>
					{I18n.get('S.E.R.A.')}
				</Checkbox>
			</LeftAlignedCol>
		);
	}

	function handleRedirectReports() {
		history.push('/reporting');
	}

	return (
		<Container>
			<ReportsBar>
				<ReportsButton type="link" onClick={handleRedirectReports}>
					{I18n.get('Reports')}
				</ReportsButton>
			</ReportsBar>
			{!!files?.length && (
				<List
					header={<HeaderList />}
					dataSource={files}
					renderItem={(item) => (
						<List.Item key={item.id}>
							<ItemRow align="middle" justify="center">
								<DescriptionCol sm={hasSeraReport ? 14 : 18}>
									<VideoIcon size={30} />
									<FileName>{item.name}</FileName>
								</DescriptionCol>
								{hasSeraReport && showSeraCheckbox(item)}
								<CenteredCol sm={2}>
									<Checkbox
										checked={item.blur}
										disabled={item.blocking}
										onChange={({ target }) =>
											onUpdate(item.id, {
												blur: target.checked
											})
										}
									/>
								</CenteredCol>
								<CenteredCol sm={4}>
									{!item.uploaded && !item.error && (
										<UploadProgress strokeWidth={10} value={item.progress} />
									)}
									{!item.error && item.uploaded && <SuccessIcon size={24} />}
									{item.error && <ErrorIcon size={24} />}
									{!item.progress && (
										<Popconfirm
											okText="Ok"
											placement="topRight"
											title={I18n.get('Are you sure?')}
											cancelText={I18n.get('Cancel')}
											onConfirm={() => onDelete(item.id)}
											icon={<DeleteConfirmIcon />}
										>
											<DeleteIcon size={24} />
										</Popconfirm>
									)}
								</CenteredCol>
							</ItemRow>
						</List.Item>
					)}
				/>
			)}
		</Container>
	);
}
